/**
 * PickingPrintModal - Modern print modal for picking operations
 * Uses printingService.ts with 4-level hierarchical configuration
 */
import React, { useState, useEffect } from 'react';
import {
  X, Tag, Package, FileText, MapPin, Loader2, Check,
  AlertCircle, ChevronLeft, Minus, Plus
} from 'lucide-react';
import { printingService } from '../../../../../services/printingService';
import {
  PrintItemLabelWithSORequest,
  PrintPackingSlipRequest,
  PrintLocationLabelRequest,
  PrintResult,
  PackingSlipLine,
  CreatePackageLabelsRequest,
  CreatePackageLabelsResponse
} from '../../../../../types/printing';
import { PListDetailsDto, PListLineDto } from '../../../../../types/picking';
import { useAuth } from '../../../../../contexts/AuthContext';

// Print types available in picking module
type PrintType = 'item' | 'itemWithOrder' | 'package' | 'packingSlip' | 'location';

interface PrintTypeConfig {
  id: PrintType;
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  hoverColor: string;
}

const PRINT_TYPES: PrintTypeConfig[] = [
  {
    id: 'item',
    label: 'Vareetikett',
    description: 'Standard vareetikett med strekkode',
    icon: <Tag size={28} />,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    hoverColor: 'hover:bg-blue-100'
  },
  {
    id: 'itemWithOrder',
    label: 'Vareetikett m/Ordre',
    description: 'Vareetikett med kundenavn og ordreinfo',
    icon: <Tag size={28} />,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    hoverColor: 'hover:bg-purple-100'
  },
  {
    id: 'package',
    label: 'Kolli-etikett',
    description: 'Oppretter kolli-rader og skriver ut etiketter',
    icon: <Package size={28} />,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    hoverColor: 'hover:bg-orange-100'
  },
  {
    id: 'packingSlip',
    label: 'Pakkseddel',
    description: 'Komplett plukkliste-dokumentasjon',
    icon: <FileText size={28} />,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    hoverColor: 'hover:bg-green-100'
  },
  {
    id: 'location',
    label: 'Lokasjonsetikett',
    description: 'Hyllekant/lokasjonsetikett',
    icon: <MapPin size={28} />,
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    hoverColor: 'hover:bg-teal-100'
  }
];

interface PickingPrintModalProps {
  isOpen: boolean;
  onClose: () => void;
  pickingDetails: PListDetailsDto | null;
  activeLine: PListLineDto | null;
}

export const PickingPrintModal: React.FC<PickingPrintModalProps> = ({
  isOpen,
  onClose,
  pickingDetails,
  activeLine
}) => {
  const { user } = useAuth();
  const [selectedType, setSelectedType] = useState<PrintType | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printResult, setPrintResult] = useState<PrintResult | null>(null);
  const [packageResult, setPackageResult] = useState<CreatePackageLabelsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Form state for editable fields
  const [copies, setCopies] = useState(1);
  const [itemNo, setItemNo] = useState('');
  const [itemDescription, setItemDescription] = useState('');
  const [quantity, setQuantity] = useState<number | null>(null);
  const [batch, setBatch] = useState('');
  const [locationName, setLocationName] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [orderNoERP, setOrderNoERP] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedType(null);
      setPrintResult(null);
      setPackageResult(null);
      setError(null);
      setCopies(1);
      prefillFromActiveLine();
    }
  }, [isOpen, activeLine]);

  // Prefill form from active line
  const prefillFromActiveLine = () => {
    if (activeLine) {
      setItemNo(activeLine.itemNo || '');
      setQuantity(activeLine.qtyPicked || activeLine.qty);
      setLocationName(activeLine.binCode || '');
      setCustomerName(activeLine.customerName || '');
      setOrderNoERP(activeLine.orderNoERP || '');
    } else if (pickingDetails?.lines?.length) {
      // Use first line as fallback
      const firstLine = pickingDetails.lines[0];
      setItemNo(firstLine.itemNo || '');
      setQuantity(firstLine.qty);
      setLocationName(firstLine.binCode || '');
      setCustomerName(firstLine.customerName || '');
      setOrderNoERP(firstLine.orderNoERP || '');
    }
  };

  // Update form when type changes
  useEffect(() => {
    if (selectedType) {
      prefillFromActiveLine();
    }
  }, [selectedType]);

  const handleBack = () => {
    setSelectedType(null);
    setPrintResult(null);
    setPackageResult(null);
    setError(null);
  };

  const handlePrint = async () => {
    if (!user?.companyNo || !selectedType) return;

    setIsPrinting(true);
    setError(null);
    setPrintResult(null);
    setPackageResult(null);

    try {
      let result: PrintResult;

      switch (selectedType) {
        case 'item':
          result = await printingService.printItemLabel(user.companyNo, {
            itemNo,
            itemDescription,
            quantity,
            batch: batch || undefined,
            locationName: locationName || undefined,
            numOfCopies: copies
          });
          break;

        case 'itemWithOrder':
          result = await printingService.printItemLabelWithSO(user.companyNo, {
            itemNo,
            itemDescription,
            quantity,
            batch: batch || undefined,
            locationName: locationName || undefined,
            orderNo: activeLine?.orderNo,
            orderNoERP: orderNoERP || undefined,
            customerName: customerName || undefined,
            numOfCopies: copies
          } as PrintItemLabelWithSORequest);
          break;

        case 'package':
          // Use the new createAndPrintPackageLabels which creates PListOrderPackages records
          // Each label creates a separate row with unique PackageID
          const packageRequest: CreatePackageLabelsRequest = {
            numberOfLabels: copies,
            pListID: pickingDetails?.header.pListID || 0,
            orderNo: activeLine?.orderNo || pickingDetails?.lines[0]?.orderNo || 0,
            pickType: 'Standalone', // TODO: Detect batch vs standalone from pList type
            customerName: customerName || undefined,
            orderNoERP: orderNoERP || undefined,
            deliveryAddress: deliveryAddress || undefined,
            printLabels: true
          };

          const packageResponse = await printingService.createAndPrintPackageLabels(packageRequest);
          setPackageResult(packageResponse);
          setIsPrinting(false);
          return; // Exit early since we handled the result differently

        case 'packingSlip':
          // Build lines from picking details
          const slipLines: PackingSlipLine[] = pickingDetails?.lines.map((line, idx) => ({
            lineNo: idx + 1,
            itemNo: line.itemNo || '',
            itemDescription: '', // Could be fetched if needed
            qtyOrdered: line.qty,
            qtyPicked: line.qtyPicked,
            location: line.binCode || undefined
          })) || [];

          result = await printingService.printPackingSlip(user.companyNo, {
            pListId: pickingDetails?.header.pListID || 0,
            customerName: customerName || undefined,
            deliveryAddress: deliveryAddress || undefined,
            packingDate: new Date().toISOString(),
            lines: slipLines,
            numOfCopies: copies
          } as PrintPackingSlipRequest);
          break;

        case 'location':
          result = await printingService.printLocationLabel(user.companyNo, {
            locationId: locationName,
            locationName: locationName || undefined,
            binCode: locationName || undefined,
            includeBarcode: true,
            numOfCopies: copies
          } as PrintLocationLabelRequest);
          break;

        default:
          throw new Error('Ukjent print-type');
      }

      setPrintResult(result);
    } catch (err: any) {
      console.error('Print error:', err);
      setError(err.message || 'Feil ved utskrift');
    } finally {
      setIsPrinting(false);
    }
  };

  if (!isOpen) return null;

  const selectedConfig = PRINT_TYPES.find(t => t.id === selectedType);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            {selectedType && (
              <button
                onClick={handleBack}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                aria-label="Tilbake til valg"
              >
                <ChevronLeft size={20} />
              </button>
            )}
            <h2 className="text-lg font-semibold text-gray-900">
              {selectedType ? selectedConfig?.label : 'Velg utskriftstype'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            aria-label="Lukk"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {!selectedType ? (
            // Type selection view - big buttons
            <div className="grid gap-3">
              {PRINT_TYPES.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setSelectedType(type.id)}
                  className={`flex items-center gap-4 p-4 rounded-xl border-2 border-transparent
                    ${type.bgColor} ${type.hoverColor} transition-all duration-200
                    hover:border-current hover:shadow-md`}
                >
                  <div className={`${type.color} p-3 bg-white rounded-xl shadow-sm`}>
                    {type.icon}
                  </div>
                  <div className="text-left flex-1">
                    <h3 className={`font-semibold ${type.color}`}>{type.label}</h3>
                    <p className="text-sm text-gray-600">{type.description}</p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            // Detail/edit view
            <div className="space-y-4">
              {/* Success/Error messages */}
              {printResult && (
                <div className={`p-4 rounded-lg flex items-start gap-3 ${
                  printResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                }`}>
                  {printResult.success ? (
                    <Check size={20} className="flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                  )}
                  <div>
                    <p className="font-medium">{printResult.success ? 'Utskrift vellykket!' : 'Feil ved utskrift'}</p>
                    <p className="text-sm">{printResult.message}</p>
                    {printResult.labelsPrinted > 0 && (
                      <p className="text-sm mt-1">{printResult.labelsPrinted} etikett(er) skrevet ut</p>
                    )}
                  </div>
                </div>
              )}

              {/* Package creation result */}
              {packageResult && (
                <div className={`p-4 rounded-lg flex items-start gap-3 ${
                  packageResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                }`}>
                  {packageResult.success ? (
                    <Check size={20} className="flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                  )}
                  <div>
                    <p className="font-medium">
                      {packageResult.success ? 'Kolli-etiketter opprettet!' : 'Feil ved opprettelse'}
                    </p>
                    <p className="text-sm">{packageResult.message}</p>
                    {packageResult.success && packageResult.createdPackages.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <p className="text-xs font-medium">Opprettede kollier:</p>
                        {packageResult.createdPackages.map((pkg) => (
                          <p key={pkg.packageID} className="text-xs">
                            {pkg.displayText} (ID: {pkg.packageID})
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {error && (
                <div className="p-4 rounded-lg bg-red-50 text-red-800 flex items-start gap-3">
                  <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Feil</p>
                    <p className="text-sm">{error}</p>
                  </div>
                </div>
              )}

              {/* Form fields based on selected type */}
              <div className="space-y-4">
                {/* Copies - always shown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {selectedType === 'package' ? 'Antall kolli-etiketter' : 'Antall kopier'}
                  </label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setCopies(Math.max(1, copies - 1))}
                      className="p-3 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                      disabled={copies <= 1}
                    >
                      <Minus size={18} />
                    </button>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={copies}
                      onChange={(e) => setCopies(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                      className="w-20 text-center p-3 border border-gray-300 rounded-lg text-lg font-semibold"
                    />
                    <button
                      type="button"
                      onClick={() => setCopies(Math.min(100, copies + 1))}
                      className="p-3 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                      disabled={copies >= 100}
                    >
                      <Plus size={18} />
                    </button>
                  </div>
                </div>

                {/* Item fields */}
                {(selectedType === 'item' || selectedType === 'itemWithOrder') && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Varenummer
                      </label>
                      <input
                        type="text"
                        value={itemNo}
                        onChange={(e) => setItemNo(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        placeholder="F.eks. 12345"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Antall
                      </label>
                      <input
                        type="number"
                        value={quantity || ''}
                        onChange={(e) => setQuantity(e.target.value ? parseInt(e.target.value) : null)}
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        placeholder="Antall enheter"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Batch/Lot (valgfritt)
                      </label>
                      <input
                        type="text"
                        value={batch}
                        onChange={(e) => setBatch(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        placeholder="F.eks. LOT2024-001"
                      />
                    </div>
                  </>
                )}

                {/* Order fields */}
                {selectedType === 'itemWithOrder' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Kundenavn
                      </label>
                      <input
                        type="text"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        placeholder="Kundens navn"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Ordrenummer (ERP)
                      </label>
                      <input
                        type="text"
                        value={orderNoERP}
                        onChange={(e) => setOrderNoERP(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        placeholder="F.eks. SO-2024-0001"
                      />
                    </div>
                  </>
                )}

                {/* Package fields */}
                {selectedType === 'package' && (
                  <>
                    <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                      <p className="text-sm text-orange-800 font-medium">
                        Oppretter {copies} kolli-rad(er) i databasen
                      </p>
                      <p className="text-xs text-orange-700 mt-1">
                        Hver kolli får et unikt ID som skrives ut som strekkode.
                        PListID: {pickingDetails?.header.pListID}, Ordre: {activeLine?.orderNo || pickingDetails?.lines[0]?.orderNo}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Kundenavn
                      </label>
                      <input
                        type="text"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Leveringsadresse
                      </label>
                      <textarea
                        value={deliveryAddress}
                        onChange={(e) => setDeliveryAddress(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        rows={2}
                        placeholder="Adresse for levering"
                      />
                    </div>
                  </>
                )}

                {/* Packing slip info */}
                {selectedType === 'packingSlip' && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">
                      Pakkseddel genereres med alle linjer fra plukkliste #{pickingDetails?.header.pListID}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      {pickingDetails?.lines.length || 0} linjer inkluderes
                    </p>
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Kundenavn (valgfritt)
                      </label>
                      <input
                        type="text"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                )}

                {/* Location fields */}
                {selectedType === 'location' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Lokasjon / Bin-kode
                    </label>
                    <input
                      type="text"
                      value={locationName}
                      onChange={(e) => setLocationName(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg"
                      placeholder="F.eks. A-01-01"
                    />
                  </div>
                )}

                {/* Context info */}
                {activeLine && (
                  <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                    <p className="font-medium">Fra aktiv plukklinje:</p>
                    <p>Vare: {activeLine.itemNo}, Lokasjon: {activeLine.binCode}</p>
                  </div>
                )}
              </div>

              {/* Print button */}
              <button
                onClick={handlePrint}
                disabled={isPrinting}
                className={`w-full py-4 rounded-xl font-semibold text-white transition-all
                  ${isPrinting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : `${selectedConfig?.color.replace('text-', 'bg-')} hover:opacity-90`
                  }`}
              >
                {isPrinting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 size={20} className="animate-spin" />
                    Skriver ut...
                  </span>
                ) : (
                  `Skriv ut ${selectedConfig?.label}`
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PickingPrintModal;
